/*
 * FROSTBEULE – product image lightbox (owner: PRODUCT). Vanilla JS, loaded with defer.
 * <fb-lightbox> wraps a native <dialog> (top layer, opened with showModal()).
 *   open(index, trigger) / close() / go(index)
 * Keyboard: ←/→ previous/next, + / − zoom, Esc closes (FROSTBEULE.focusTrap + dialog "cancel").
 * Open/close: CSS transition on .is-open (backdrop fade + image settle), skipped when motion = none.
 * Swipe: native CSS scroll-snap on the track. Zoom: click/tap on the image or the zoom button
 * (2.5×, centred on the pointer); while zoomed the slide pans natively (wheel/touch) and by
 * mouse drag. On close it fires `fb:lightbox-close` (detail.index = image last seen) so the gallery
 * moves there; focus returns to that image's zoom button (else to the one that opened the viewer).
 */
(function () {
  'use strict';

  if (customElements.get('fb-lightbox')) return;

  var C = window.FROSTBEULE || {};
  var ZOOM = 2.5;

  function motion() {
    return typeof C.motion === 'function' ? C.motion() : 'full';
  }

  class FbLightbox extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.dialog = this.querySelector('[data-fb-lb-dialog]');
      this.track = this.querySelector('[data-fb-lb-track]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-fb-lb-slide]'));
      this.counter = this.querySelector('[data-fb-lb-current]');
      this.live = this.querySelector('[data-fb-lb-live]');
      this.zoomBtn = this.querySelector('[data-fb-lb-zoom]');
      this.current = 0;
      this.isOpen = false;
      if (!this.dialog || !this.track) return;

      this.onClick = this.onClick.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onCancel = this.onCancel.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);

      this.dialog.addEventListener('click', this.onClick);
      this.dialog.addEventListener('keydown', this.onKey);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.onPointerDown);
    }

    disconnectedCallback() {
      if (this.isOpen) this.close(true);
      this._bound = false;
      if (!this.dialog || !this.track) return;
      this.dialog.removeEventListener('click', this.onClick);
      this.dialog.removeEventListener('keydown', this.onKey);
      this.dialog.removeEventListener('cancel', this.onCancel);
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('pointerdown', this.onPointerDown);
    }

    open(index, trigger) {
      if (!this.dialog || !this.slides.length) return;
      if (this.isOpen) {
        this.go(index, false);
        return;
      }
      var self = this;
      this.isOpen = true;
      this.trigger = trigger || document.activeElement;
      if (this.closeTimer) clearTimeout(this.closeTimer);

      if (typeof this.dialog.showModal === 'function') {
        try {
          if (!this.dialog.open) this.dialog.showModal();
        } catch (e) {
          this.dialog.setAttribute('open', '');
        }
      } else {
        this.dialog.setAttribute('open', '');
      }
      if (typeof C.lockScroll === 'function') C.lockScroll();
      window.addEventListener('resize', this.onResize);

      this.current = -1;
      this.go(index || 0, false);

      requestAnimationFrame(function () {
        self.classList.add('is-open');
      });

      if (typeof C.focusTrap === 'function') {
        this.trap = C.focusTrap(this.dialog, {
          initialFocus: '[data-fb-lb-close]',
          onEscape: function () {
            self.close();
          },
        });
      }
    }

    close(immediate) {
      if (!this.isOpen) return;
      var self = this;
      this.isOpen = false;
      if (!immediate) {
        /* let the gallery follow to the image last seen here; it may hand back the matching
           zoom button as the new focus target (detail.trigger) */
        var detail = { index: this.current, trigger: this.trigger };
        this.dispatchEvent(new CustomEvent('fb:lightbox-close', { bubbles: true, detail: detail }));
        if (detail.trigger) this.trigger = detail.trigger;
      }
      this.unzoomAll();
      this.classList.remove('is-open');
      window.removeEventListener('resize', this.onResize);
      if (this.trap) {
        this.trap.release(false);
        this.trap = null;
      }

      var finish = function () {
        self.closeTimer = null;
        if (self.dialog.open) {
          try {
            self.dialog.close();
          } catch (e) {
            self.dialog.removeAttribute('open');
          }
        }
        if (typeof C.unlockScroll === 'function') C.unlockScroll();
        var trigger = self.trigger;
        self.trigger = null;
        if (!immediate && trigger && document.contains(trigger) && typeof trigger.focus === 'function') {
          try {
            trigger.focus({ preventScroll: true });
          } catch (e) {
            trigger.focus();
          }
        }
      };

      if (immediate || motion() === 'none') finish();
      else this.closeTimer = setTimeout(finish, 220);
    }

    onCancel(e) {
      e.preventDefault();
      this.close();
    }

    go(index, smooth) {
      var n = this.slides.length;
      if (!n) return;
      index = ((index % n) + n) % n;
      this.unzoomAll();
      var left = index * this.track.clientWidth;
      if (smooth && motion() === 'full') this.track.scrollTo({ left: left, behavior: 'smooth' });
      else this.track.scrollLeft = left;
      this.setCurrent(index);
    }

    setCurrent(index) {
      if (index === this.current) return;
      this.current = index;
      if (this.counter) this.counter.textContent = String(index + 1);
      if (this.live) {
        var tpl = (C.strings && C.strings.slideOf) || '[current] / [total]';
        this.live.textContent = tpl.replace('[current]', index + 1).replace('[total]', this.slides.length);
      }
    }

    onScroll() {
      if (this.raf || this.track.classList.contains('is-zoomed')) return;
      var self = this;
      this.raf = requestAnimationFrame(function () {
        self.raf = null;
        var w = self.track.clientWidth || 1;
        var index = Math.max(0, Math.min(self.slides.length - 1, Math.round(self.track.scrollLeft / w)));
        if (index !== self.current) self.setCurrent(index);
      });
    }

    onResize() {
      if (!this.isOpen) return;
      this.unzoomAll();
      this.track.scrollLeft = this.current * this.track.clientWidth;
    }

    onKey(e) {
      if (e.key === 'ArrowLeft' || e.key === 'Left') {
        e.preventDefault();
        this.go(this.current - 1, true);
      } else if (e.key === 'ArrowRight' || e.key === 'Right') {
        e.preventDefault();
        this.go(this.current + 1, true);
      } else if (e.key === '+' || e.key === '=' || e.key === '-' || e.key === '_') {
        var slide = this.slides[this.current];
        var zoomed = !!(slide && slide.classList.contains('is-zoomed'));
        var wantIn = e.key === '+' || e.key === '=';
        if (wantIn !== zoomed) {
          e.preventDefault();
          this.toggleZoom(this.current, null);
        }
      }
    }

    onClick(e) {
      if (this.suppressClick) {
        this.suppressClick = false;
        return;
      }
      var t = e.target;
      if (t.closest('[data-fb-lb-close]')) return this.close();
      if (t.closest('[data-fb-lb-prev]')) return this.go(this.current - 1, true);
      if (t.closest('[data-fb-lb-next]')) return this.go(this.current + 1, true);
      if (t.closest('[data-fb-lb-zoom]')) return this.toggleZoom(this.current, null);

      var slide = t.closest('[data-fb-lb-slide]');
      if (!slide) return;
      var index = this.slides.indexOf(slide);
      if (t.closest('img')) {
        this.toggleZoom(index, e);
      } else if (!slide.classList.contains('is-zoomed')) {
        /* click on the dark backdrop around the image */
        this.close();
      }
    }

    toggleZoom(index, evt) {
      var slide = this.slides[index];
      if (!slide) return;
      if (slide.classList.contains('is-zoomed')) {
        this.unzoom(slide);
        return;
      }
      var pan = slide.querySelector('[data-fb-lb-pan]');
      var img = pan && pan.querySelector('img');
      if (!img) return;

      var rect = img.getBoundingClientRect();
      var fx = 0.5;
      var fy = 0.5;
      if (evt && rect.width && rect.height) {
        fx = Math.max(0, Math.min(1, (evt.clientX - rect.left) / rect.width));
        fy = Math.max(0, Math.min(1, (evt.clientY - rect.top) / rect.height));
      }
      var w = Math.round(rect.width * ZOOM);
      var h = Math.round(rect.height * ZOOM);

      slide.classList.add('is-zoomed');
      this.track.classList.add('is-zoomed');
      img.style.width = w + 'px';
      img.style.height = h + 'px';
      if (img.getAttribute('srcset')) img.setAttribute('sizes', w + 'px');

      pan.scrollLeft = fx * w - pan.clientWidth / 2;
      pan.scrollTop = fy * h - pan.clientHeight / 2;
      this.setZoomButton(true);
    }

    unzoom(slide) {
      var img = slide.querySelector('img');
      slide.classList.remove('is-zoomed');
      if (img) {
        img.style.width = '';
        img.style.height = '';
        if (img.getAttribute('srcset')) img.setAttribute('sizes', '100vw');
      }
      if (!this.track.querySelector('.is-zoomed')) this.track.classList.remove('is-zoomed');
      this.setZoomButton(false);
    }

    unzoomAll() {
      var self = this;
      this.slides.forEach(function (slide) {
        if (slide.classList.contains('is-zoomed')) self.unzoom(slide);
      });
    }

    setZoomButton(zoomed) {
      if (!this.zoomBtn) return;
      this.zoomBtn.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
      var label = zoomed ? this.zoomBtn.getAttribute('data-label-out') : this.zoomBtn.getAttribute('data-label-in');
      if (label) this.zoomBtn.setAttribute('aria-label', label);
    }

    /* mouse drag to pan while zoomed (touch pans natively) */
    onPointerDown(e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      var slide = e.target.closest('.is-zoomed');
      if (!slide) return;
      var pan = slide.querySelector('[data-fb-lb-pan]');
      e.preventDefault();
      this.drag = { pan: pan, x: e.clientX, y: e.clientY, left: pan.scrollLeft, top: pan.scrollTop, moved: false };
      try {
        pan.setPointerCapture(e.pointerId);
      } catch (err) {
        /* ignore */
      }
      pan.addEventListener('pointermove', this.onPointerMove);
      pan.addEventListener('pointerup', this.onPointerUp);
      pan.addEventListener('pointercancel', this.onPointerUp);
      slide.classList.add('is-dragging');
    }

    onPointerMove(e) {
      var d = this.drag;
      if (!d) return;
      var dx = e.clientX - d.x;
      var dy = e.clientY - d.y;
      if (Math.abs(dx) + Math.abs(dy) > 4) d.moved = true;
      d.pan.scrollLeft = d.left - dx;
      d.pan.scrollTop = d.top - dy;
    }

    onPointerUp() {
      var d = this.drag;
      if (!d) return;
      d.pan.removeEventListener('pointermove', this.onPointerMove);
      d.pan.removeEventListener('pointerup', this.onPointerUp);
      d.pan.removeEventListener('pointercancel', this.onPointerUp);
      var slide = d.pan.closest('[data-fb-lb-slide]');
      if (slide) slide.classList.remove('is-dragging');
      this.suppressClick = d.moved;
      this.drag = null;
      var self = this;
      setTimeout(function () {
        self.suppressClick = false;
      }, 0);
    }
  }

  customElements.define('fb-lightbox', FbLightbox);
})();
